/** Where the corners are on one lap: detected from the racing line's
 *  curvature, or the circuit's authored corners placed on the lap's own
 *  distance axis.
 *
 * Detection runs per lap, and its parameters were tuned against real GT7
 * laps (5 sessions, road courses + a banked oval) for IDENTICAL corner counts
 * and <30 m apex drift across laps of the same track — numbered corners that
 * renumber between laps are useless. Authored corners (#48) outrank it for
 * the same reason: a lap that carries less speed through a shallow bend may
 * not register it as a corner at all, and every corner after it renumbers.
 * Cross-lap and cross-session comparison cannot rest on numbering that
 * moves. Authored corners are stable by construction; all a lap contributes
 * is where along ITS distance axis they fell.
 */

import { column } from './columns';
import { at, bisectLeft, interp, minOf } from './interp';
import { roundTo } from './round';
import type { AuthoredCorner, AuthoredSection, Corner, Samples, Section } from './types';

// --- Corner detection -------------------------------------------------------

// The hysteresis band is deliberately narrow: K_HI above ~0.0035 loses
// banked/high-speed corners entirely, K_LO below ~0.002 sinks into the
// road-noise floor and bleeds adjacent corners together.
export const CORNER_STEP_M = 2; // uniform resample grid for curvature
export const CORNER_HALF_WIN_M = 16; // heading measured over ±this many metres
// The enter/stay thresholds adapt to each lap's own curvature noise (see
// thresholds). Calibrated by sweeping candidates over real road courses,
// a banked oval, and the simulator: noisy real telemetry (jitter p85
// ~0.0004) must run the validated 0.0030/0.0022 — anything lower flips
// counts between laps; smooth data (jitter < 0.0001) safely drops to
// 0.0020/0.0013, which recovers broad-radius corners without growing
// phantoms (going lower DID grow one on the oval).
export const CORNER_K_LO_MIN = 0.0013; // rad/m — floor for the stay threshold
export const CORNER_K_LO_MAX = 0.0022; // rad/m — validated real-track value
export const CORNER_K_HI_RATIO = 1.54; // enter = stay * ratio ...
export const CORNER_K_HI_MAX = 0.003; // ... capped at the validated real-track value
// Stay threshold = this × the p85 curvature jitter. Measured jitter: real
// GT7 laps 0.00029-0.00052 (×8 clamps them all to K_LO_MAX), smooth
// sim/parametric data 0.00005-0.00008 (lands on K_LO_MIN with 2× margin).
export const CORNER_NOISE_SCALE = 8;
export const CORNER_END_GAP_M = 40; // below the stay threshold for this long ends the segment
export const CORNER_MERGE_GAP_M = 90; // same-direction arcs closer than this merge
export const CORNER_NOISE_ANGLE_DEG = 12; // arcs below this are noise, dropped pre-merge
export const CORNER_MIN_ANGLE_DEG = 25; // final significance threshold
export const CORNER_MAX_ANGLE_DEG = 300; // beyond this it's a spin, not a corner

const RAD_TO_DEG = 180 / Math.PI;

/** Python's `math.degrees`. */
export function degrees(rad: number): number {
  return rad * RAD_TO_DEG;
}

/** Wrap to (-pi, pi]. */
export function wrapAngle(a: number): number {
  while (a <= -Math.PI) a += 2 * Math.PI;
  while (a > Math.PI) a -= 2 * Math.PI;
  return a;
}

interface Arc {
  start: number; // indices into the resampled grid
  end: number; // inclusive
  sign: number;
  angle: number; // total heading change, radians (signed)
  /** Second index range of a start/finish-stitched corner (the post-line
   *  half at the beginning of the lap); null for ordinary corners. */
  wrap: [number, number] | null;
}

/** Auto-numbered corners from the racing line's signed curvature.
 *
 * Pipeline (each stage exists because the naive version failed on real
 * laps — see the logger's docs/internals/analysis-math.md):
 * resample to a uniform grid → signed curvature from wrapped chord-heading
 * deltas → hysteresis segmentation that splits on direction flips → drop
 * sub-noise arcs BEFORE merging (a surviving opposite blip would block
 * merges on some laps only) → merge same-direction arcs across short gaps
 * (a hairpin whose curvature dips mid-arc is one corner) → keep significant
 * arcs → apex at the curvature-weighted centroid (min speed sits at the
 * segment edge and wanders ~100 m between laps).
 */
export function detectCorners(samples: Samples): Corner[] {
  const dist = column(samples, 'dist') ?? [];
  const xs = column(samples, 'pos_x') ?? [];
  const zs = column(samples, 'pos_z') ?? [];
  const rawSpeed = column(samples, 'speed') ?? [];
  const n = Math.min(dist.length, xs.length, zs.length, rawSpeed.length || dist.length);
  if (n < 8) return [];
  const speed = rawSpeed.length > 0 ? rawSpeed : new Array<number>(n).fill(0);

  // Strictly increasing distance only — duplicates/backwards points (pause
  // edges, imports) break interpolation.
  const keep = [0];
  for (let i = 1; i < n; i++) {
    if (dist[i]! > dist[keep[keep.length - 1]!]!) keep.push(i);
  }
  const k = keep.length;
  if (k < 8 || dist[keep[k - 1]!]! - dist[keep[0]!]! < 8 * CORNER_STEP_M) return [];
  const d = new Array<number>(k);
  const px = new Array<number>(k);
  const pz = new Array<number>(k);
  const sp = new Array<number>(k);
  for (let j = 0; j < k; j++) {
    const i = keep[j]!;
    d[j] = dist[i]!;
    px[j] = xs[i]!;
    pz[j] = zs[i]!;
    sp[j] = speed[i]!;
  }

  // Uniform 2 m grid decouples curvature from the 60 Hz speed-dependent
  // sample spacing.
  const total = d[k - 1]! - d[0]!;
  const m = Math.trunc(total / CORNER_STEP_M) + 1;
  const grid = new Array<number>(m);
  const gx = new Array<number>(m);
  const gz = new Array<number>(m);
  const gs = new Array<number>(m);
  for (let i = 0; i < m; i++) {
    const g = d[0]! + i * CORNER_STEP_M;
    grid[i] = g;
    gx[i] = interp(d, px, g);
    gz[i] = interp(d, pz, g);
    gs[i] = interp(d, sp, g);
  }

  // Signed curvature: wrapped delta between the chord headings of the two
  // half-windows either side of each point, divided by the half-span.
  const w = Math.max(1, Math.trunc(CORNER_HALF_WIN_M / CORNER_STEP_M));
  const curv = new Array<number>(m).fill(0);
  for (let i = w; i < m - w; i++) {
    const dx0 = gx[i]! - gx[i - w]!;
    const dz0 = gz[i]! - gz[i - w]!;
    const dx1 = gx[i + w]! - gx[i]!;
    const dz1 = gz[i + w]! - gz[i]!;
    if ((dx0 === 0 && dz0 === 0) || (dx1 === 0 && dz1 === 0)) continue;
    const dh = wrapAngle(Math.atan2(dz1, dx1) - Math.atan2(dz0, dx0));
    curv[i] = dh / (w * CORNER_STEP_M);
  }

  const [kHi, kLo] = thresholds(curv);
  let arcs = segmentArcs(curv, kHi, kLo);
  // Noise arcs are discarded BEFORE merging so they cannot block a merge.
  arcs = arcs.filter((a) => Math.abs(degrees(a.angle)) >= CORNER_NOISE_ANGLE_DEG);
  arcs = mergeArcs(arcs);
  // Stitch BEFORE the significance filter: a corner split by the start line
  // must be judged on its combined angle, not per half (a 40° corner split
  // 20/20 would otherwise vanish). The spin cap also applies post-stitch.
  arcs = stitchWraparound(arcs, m);
  arcs = arcs.filter((a) => {
    const deg = Math.abs(degrees(a.angle));
    return CORNER_MIN_ANGLE_DEG <= deg && deg <= CORNER_MAX_ANGLE_DEG;
  });

  const corners: Corner[] = [];
  const withApex = arcs
    .map((a): [Arc, number] => [a, apexIndex(a, curv)])
    .sort((p, q) => p[1] - q[1]);
  for (const [a, i] of withApex) {
    // A stitched start/finish corner spans the lap boundary: its extent
    // wraps (entry_dist > exit_dist) and min_speed covers both halves.
    const entry = a.start;
    let exit = a.end;
    let speeds = gs.slice(a.start, a.end + 1);
    if (a.wrap !== null) {
      exit = a.wrap[1];
      speeds = speeds.concat(gs.slice(a.wrap[0], a.wrap[1] + 1));
    }
    corners.push({
      n: corners.length + 1,
      apex_dist: roundTo(at(grid, i), 1),
      apex_x: roundTo(at(gx, i), 1),
      apex_z: roundTo(at(gz, i), 1),
      entry_dist: roundTo(at(grid, entry), 1),
      exit_dist: roundTo(at(grid, exit), 1),
      // Positive heading delta is CCW in raw x/z, but the map (and GT7's own
      // view) renders z inverted — that's a right-hander.
      direction: a.sign > 0 ? 'R' : 'L',
      min_speed: roundTo(minOf(speeds), 1),
      angle_deg: roundTo(Math.abs(degrees(a.angle)), 1),
    });
  }
  return corners;
}

/** Hysteresis thresholds anchored to this lap's curvature noise floor.
 *
 * The floor is the high end of the frame-to-frame curvature jitter — real
 * GT7 telemetry wobbles around zero on straights, smooth data barely at
 * all. The stay threshold must clear that jitter or adjacent corners bleed
 * together; but fixing it at the real-track value silently drops the
 * broad-radius corners of clean low-curvature tracks.
 */
function thresholds(curv: readonly number[]): [number, number] {
  const jitter = new Float64Array(Math.max(0, curv.length - 1));
  for (let i = 1; i < curv.length; i++) jitter[i - 1] = Math.abs(curv[i]! - curv[i - 1]!);
  jitter.sort();
  const noise = jitter.length > 0 ? jitter[Math.trunc(jitter.length * 0.85)]! : 0;
  const kLo = Math.min(CORNER_K_LO_MAX, Math.max(CORNER_K_LO_MIN, noise * CORNER_NOISE_SCALE));
  const kHi = Math.min(kLo * CORNER_K_HI_RATIO, CORNER_K_HI_MAX);
  return [kHi, kLo];
}

/** Hysteresis segmentation of the curvature series, split on sign flips. */
function segmentArcs(curv: readonly number[], kHi: number, kLo: number): Arc[] {
  const endGap = Math.trunc(CORNER_END_GAP_M / CORNER_STEP_M);
  const arcs: Arc[] = [];
  const m = curv.length;
  let i = 0;
  while (i < m) {
    if (Math.abs(curv[i]!) <= kHi) {
      i += 1;
      continue;
    }
    const sign = curv[i]! > 0 ? 1 : -1;
    const start = i;
    let lastActive = i;
    let angle = 0;
    while (i < m) {
      const k = curv[i]!;
      if (k * sign > 0 && Math.abs(k) >= kLo) {
        lastActive = i;
        angle += k * CORNER_STEP_M;
        i += 1;
      } else if (k * -sign > kHi) {
        break; // strong opposite curvature: an S — new corner
      } else if (i - lastActive >= endGap) {
        break; // faded out for long enough
      } else {
        i += 1;
      }
    }
    arcs.push({ start, end: lastActive, sign, angle, wrap: null });
  }
  return arcs;
}

/** Merge same-direction arcs across short gaps (double-apex complexes). */
function mergeArcs(arcs: readonly Arc[]): Arc[] {
  const gap = Math.trunc(CORNER_MERGE_GAP_M / CORNER_STEP_M);
  const out: Arc[] = [];
  for (const a of arcs) {
    const prev = out[out.length - 1];
    if (prev !== undefined && prev.sign === a.sign && a.start - prev.end <= gap) {
      prev.end = a.end;
      prev.angle += a.angle;
    } else {
      out.push({ start: a.start, end: a.end, sign: a.sign, angle: a.angle, wrap: null });
    }
  }
  return out;
}

/** A lap that starts mid-corner shows one physical corner split across
 *  the start/finish line; merge the two halves into one corner.
 *
 * Each half may sit at most half the merge gap from its lap edge, so the
 * combined tolerance matches the mid-lap merge distance. The surviving arc
 * is the pre-line half; `wrap` records the post-line half so the corner's
 * extent (entry > exit signals the wrap), min_speed, and apex consider
 * both halves.
 */
function stitchWraparound(arcs: Arc[], m: number): Arc[] {
  const edge = Math.trunc(CORNER_MERGE_GAP_M / 2 / CORNER_STEP_M);
  if (arcs.length < 2) return arcs;
  const first = arcs[0]!;
  const last = arcs[arcs.length - 1]!;
  if (first.sign === last.sign && first.start <= edge && m - 1 - last.end <= edge) {
    last.angle += first.angle;
    last.wrap = [first.start, first.end];
    return arcs.slice(1);
  }
  return arcs;
}

/** Curvature-weighted centroid: stable within ~25 m across laps, where
 *  the min-speed point wanders with braking for the NEXT corner.
 *
 * For a start/finish-stitched corner the centroid comes from whichever
 * half turns more — averaging indices across the lap seam would land the
 * apex mid-lap, nowhere near the corner.
 */
function apexIndex(a: Arc, curv: readonly number[]): number {
  const centroid = (lo: number, hi: number): [number, number] => {
    let total = 0;
    let weighted = 0;
    for (let i = lo; i <= hi; i++) {
      const w = Math.abs(curv[i]!);
      total += w;
      weighted += w * i;
    }
    const idx = total > 0 ? roundTo(weighted / total) : Math.floor((lo + hi) / 2);
    return [idx, total];
  };
  const [idx0, t0] = centroid(a.start, a.end);
  if (a.wrap === null) return idx0;
  const [idx1, t1] = centroid(a.wrap[0], a.wrap[1]);
  return t0 >= t1 ? idx0 : idx1;
}

// --- authored corners (#48) ---------------------------------------------------

/** An apex anchor further than this from anything the lap drove is not on
 *  this lap: a bundle for a different layout, or a corner marked off the
 *  road. */
export const CORNER_ANCHOR_MAX_M = 60;
/** Extent used when a corner was labelled with an apex but no entry/exit.
 *  Half of a fairly generous corner, clipped at the midpoint to its
 *  neighbours — enough for "which corner is this braking event for" without
 *  pretending the turn-in point is known. */
export const CORNER_DEFAULT_HALF_M = 75;

interface LapPath {
  d: number[];
  xs: number[];
  zs: number[];
  speeds: number[];
}

/** Strictly-increasing (dist, x, z, speed) — duplicates break projection. */
function lapPath(samples: Samples): LapPath {
  const dist = column(samples, 'dist') ?? [];
  const xs = column(samples, 'pos_x') ?? [];
  const zs = column(samples, 'pos_z') ?? [];
  const rawSpeed = column(samples, 'speed') ?? [];
  const n = Math.min(dist.length, xs.length, zs.length);
  if (n < 2) return { d: [], xs: [], zs: [], speeds: [] };
  const speed = rawSpeed.length >= n ? rawSpeed : new Array<number>(n).fill(0);
  const keep = [0];
  for (let i = 1; i < n; i++) {
    if (dist[i]! > dist[keep[keep.length - 1]!]!) keep.push(i);
  }
  const out: LapPath = { d: [], xs: [], zs: [], speeds: [] };
  for (const i of keep) {
    out.d.push(dist[i]!);
    out.xs.push(xs[i]!);
    out.zs.push(zs[i]!);
    out.speeds.push(speed[i]!);
  }
  return out;
}

/** The sample nearest a world position, and how far off it is. */
function nearestIndex(xs: readonly number[], zs: readonly number[], x: number, z: number): [number, number] {
  let bestI = 0;
  let bestD2 = Infinity;
  for (let i = 0; i < xs.length; i++) {
    const dx = xs[i]! - x;
    const dz = zs[i]! - z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) {
      bestI = i;
      bestD2 = d2;
    }
  }
  return [bestI, Math.sqrt(bestD2)];
}

/** Python's `float(value)`, or null where it would raise. */
function readFloat(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const v = Number(value);
    return Number.isNaN(v) ? null : v;
  }
  return null;
}

/** `float(point["x"]), float(point["z"])` of a bundle point, or null where
 *  the point is not a dict or either coordinate would not read. */
function readPoint(point: unknown): [number, number] | null {
  if (typeof point !== 'object' || point === null || Array.isArray(point)) return null;
  const p = point as Record<string, unknown>;
  const x = readFloat(p['x']);
  const z = readFloat(p['z']);
  return x === null || z === null ? null : [x, z];
}

/** Python's `int(value)`, with `fallback` where the key was absent. */
function readInt(value: unknown, fallback: number): number {
  if (value === undefined) return fallback;
  const v = Number(value);
  if (value === null || Number.isNaN(v)) throw new TypeError(`${String(value)} is not an integer`);
  return Math.trunc(v);
}

/** Place a circuit's authored corners on one lap's distance axis.
 *
 * Corners are anchored to world POSITIONS, not lap distances, because
 * distance depends on the racing line taken — a corner pinned at 1,240 m on
 * one lap sits somewhere else on the next. So each lap resolves its own
 * apex/entry/exit distances by finding where it passed the anchor, and the
 * identity (number, name, direction) comes from the bundle unchanged.
 */
export function projectCorners(samples: Samples, authored: readonly AuthoredCorner[]): Corner[] {
  const { d, xs, zs, speeds } = lapPath(samples);
  if (d.length < 8) return [];
  const placed: { apexDist: number; corner: AuthoredCorner; apexI: number; apex: [number, number] }[] = [];
  for (const corner of authored) {
    const apex = readPoint(corner.apex ?? {});
    if (apex === null) continue;
    const [i, gap] = nearestIndex(xs, zs, apex[0], apex[1]);
    if (gap > CORNER_ANCHOR_MAX_M) continue; // this corner is not on this lap
    placed.push({ apexDist: d[i]!, corner, apexI: i, apex });
  }
  if (placed.length === 0) return [];
  placed.sort((p, q) => p.apexDist - q.apexDist);

  const first = d[0]!;
  const last = d[d.length - 1]!;
  const out: Corner[] = [];
  for (let pos = 0; pos < placed.length; pos++) {
    const { apexDist, corner, apexI, apex } = placed[pos]!;
    const far = CORNER_DEFAULT_HALF_M * 2; // no neighbour on this side to clip to
    const prevDist = pos > 0 ? placed[pos - 1]!.apexDist : first - far;
    const nextDist = pos + 1 < placed.length ? placed[pos + 1]!.apexDist : last + far;
    let entry = anchorDist(corner.entry, d, xs, zs);
    let exit = anchorDist(corner.exit, d, xs, zs);
    if (entry === null) {
      entry = Math.max(apexDist - CORNER_DEFAULT_HALF_M, (apexDist + prevDist) / 2, first);
    }
    if (exit === null) {
      exit = Math.min(apexDist + CORNER_DEFAULT_HALF_M, (apexDist + nextDist) / 2, last);
    }
    const lo = bisectLeft(d, entry);
    const hi = bisectLeft(d, exit);
    const window = speeds.slice(lo, Math.max(hi + 1, lo + 1));
    const minSpeed = window.length > 0 ? minOf(window) : at(speeds, apexI);
    out.push({
      n: readInt(corner.n, pos + 1),
      name: String(corner.name || ''),
      // The apex is the AUTHORED position, not the nearest sample: it is
      // the same point on every lap, which is the whole point.
      apex_dist: roundTo(apexDist, 1),
      apex_x: roundTo(apex[0], 1),
      apex_z: roundTo(apex[1], 1),
      entry_dist: roundTo(entry, 1),
      exit_dist: roundTo(exit, 1),
      direction: String(corner.direction || turnDirection(xs, zs, lo, hi)),
      min_speed: roundTo(minSpeed, 1),
      angle_deg: roundTo(turnAngleDeg(xs, zs, lo, hi), 1),
      authored: true,
    });
  }
  return out;
}

/** Where this lap passed nearest an authored point, as a distance along
 *  it — or null for a point it never came near, or that is not a point. */
function anchorDist(
  point: unknown,
  d: readonly number[],
  xs: readonly number[],
  zs: readonly number[],
): number | null {
  const p = readPoint(point);
  if (p === null) return null;
  const [i, gap] = nearestIndex(xs, zs, p[0], p[1]);
  return gap <= CORNER_ANCHOR_MAX_M ? d[i]! : null;
}

/** Place a circuit's authored sections on one lap's distance axis.
 *
 * A section is a named stretch of road — "Kemmel Straight", "Backstretch" —
 * stored in the bundle as the world positions of its two ends, for the
 * reason corners are: a distance belongs to the line one lap took. Each
 * end is where this lap passed nearest to it. A section with an end this
 * lap never came near is left out rather than guessed at, and one whose
 * start is past its end runs across the start line.
 */
export function projectSections(samples: Samples, authored: readonly AuthoredSection[]): Section[] {
  const { d, xs, zs } = lapPath(samples);
  if (d.length < 8) return [];
  const out: Section[] = [];
  authored.forEach((section, index) => {
    const start = anchorDist(section.start, d, xs, zs);
    const end = anchorDist(section.end, d, xs, zs);
    if (start === null || end === null || start === end) return;
    out.push({
      n: readInt(section.n, index + 1),
      name: String(section.name || ''),
      start_dist: roundTo(start, 1),
      end_dist: roundTo(end, 1),
    });
  });
  out.sort((a, b) => a.start_dist - b.start_dist);
  return out;
}

/** Heading change the lap actually made across the corner's extent.
 *
 * Descriptive only — unlike detection, nothing here decides whether the
 * corner exists. A driver who straightlined a chicane gets a small number
 * against a corner that is still corner 7.
 */
function turnAngleDeg(xs: readonly number[], zs: readonly number[], lo: number, hi: number): number {
  if (hi - lo < 4) return 0;
  let total = 0;
  const step = Math.max(1, Math.floor((hi - lo) / 16));
  let prev: number | null = null;
  for (let i = lo; i <= hi; i += step) {
    const j = Math.min(i + step, hi);
    const dx = at(xs, j) - at(xs, i);
    const dz = at(zs, j) - at(zs, i);
    if (dx === 0 && dz === 0) continue;
    const heading = Math.atan2(dz, dx);
    if (prev !== null) total += wrapAngle(heading - prev);
    prev = heading;
  }
  return Math.abs(degrees(total));
}

function turnDirection(xs: readonly number[], zs: readonly number[], lo: number, hi: number): string {
  if (hi - lo < 4) return 'R';
  const dx0 = at(xs, lo + 1) - at(xs, lo);
  const dz0 = at(zs, lo + 1) - at(zs, lo);
  const dx1 = at(xs, hi) - at(xs, hi - 1);
  const dz1 = at(zs, hi) - at(zs, hi - 1);
  const turn = wrapAngle(Math.atan2(dz1, dx1) - Math.atan2(dz0, dx0));
  // Positive heading delta is CCW in raw x/z, but the map (and GT7's own
  // view) renders z inverted — that's a right-hander.
  return turn > 0 ? 'R' : 'L';
}

/** A lap's corners: the circuit's authored ones if it has any, else detected.
 *
 * Falling back on an empty projection is deliberate — an authored set that
 * places nothing on this lap means the bundle describes a different layout,
 * and generic corners beat no corners.
 */
export function cornersForLap(samples: Samples, authored?: readonly AuthoredCorner[] | null): Corner[] {
  if (authored !== undefined && authored !== null && authored.length > 0) {
    const projected = projectCorners(samples, authored);
    if (projected.length > 0) return projected;
  }
  return detectCorners(samples);
}
